import React, { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Form,
  Card,
  Tab,
  Tabs,
  Dropdown,
  ListGroup,
  Spinner,
  Badge,
  Collapse,
  InputGroup,
} from 'react-bootstrap';
import {
  ChevronDown,
  ChevronUp,
  ThreeDotsVertical,
  Plus,
  Person,
  PersonCircle,
  Building,
  CurrencyDollar,
  Calendar3,
  ListUl,
  BarChartSteps,
  Gear,
  Check2,
  PencilSquare,
  X,
} from 'react-bootstrap-icons';
import {
  useGetProjectQuery,
  useUpdateProjectMutation,
  useGetTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useGetUserPreferencesQuery,
} from '../../services/api';
import { useUserPreferencesQueryArg } from '../../hooks/useTenantIdForPreferences';
import ERMSectionHeader from './ERMSectionHeader';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
} from '../../utils/tenantTimezone';

const STAGES = ['Kick-off', 'Planning', 'Implementation', 'Review', 'Closing'];

export interface ErmTask {
  id: string;
  projectId?: string;
  phase?: string;
  subject?: string;
  assigneeId?: string;
  dueDate?: string;
  done?: boolean;
  group?: string;
  sortOrder?: number;
  [key: string]: unknown;
}

export default function ERMProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('plan');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [planView, setPlanView] = useState<'list' | 'gantt'>('list');
  const [newTaskSubject, setNewTaskSubject] = useState('');
  const [newTaskPhase, setNewTaskPhase] = useState('Phase unassigned');
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [isEditingProjectTitle, setIsEditingProjectTitle] = useState(false);
  const [projectTitleDraft, setProjectTitleDraft] = useState('');

  const { data: project, isLoading: projectLoading, refetch: refetchProject } = useGetProjectQuery(projectId!, { skip: !projectId });
  const { data: tasks = [], isLoading: tasksLoading, refetch: refetchTasks } = useGetTasksQuery(
    { projectId: projectId!, archived: false },
    { skip: !projectId }
  );
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );
  const [updateProject, { isLoading: isUpdatingProject }] = useUpdateProjectMutation();
  const [createTask, { isLoading: creatingTask }] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();

  const currentProjectTitle = String(project?.title || '').trim() || 'Untitled project';
  const status = project?.status || 'TO-DO';
  const stage = project?.stage || STAGES[0];
  const stageIndex = STAGES.indexOf(stage);

  const tasksByPhase = React.useMemo((): Record<string, ErmTask[]> => {
    const map: Record<string, ErmTask[]> = {};
    tasks.forEach((t: ErmTask) => {
      const p = t.phase || 'Phase unassigned';
      if (!map[p]) map[p] = [];
      map[p].push(t);
    });
    const phases = ['Phase unassigned', ...STAGES];
    phases.forEach((p) => { if (!map[p]) map[p] = []; });
    return map;
  }, [tasks]);

  const handleStatus = async (newStatus: 'TO-DO' | 'Completed' | 'Canceled') => {
    try {
      await updateProject({ projectId: projectId!, status: newStatus }).unwrap();
      refetchProject();
    } catch (e) {
      console.error(e);
    }
  };

  const handleStage = async (newStage: string) => {
    try {
      await updateProject({ projectId: projectId!, stage: newStage }).unwrap();
      refetchProject();
    } catch (e) {
      console.error(e);
    }
  };

  const handleArchiveProject = async () => {
    if (!window.confirm('Archive this project? You can permanently delete it from Archive.')) return;
    try {
      await updateProject({ projectId: projectId!, archived: true }).unwrap();
      navigate('/erm/archive');
    } catch (e) {
      console.error(e);
    }
  };

  const startRenameProject = () => {
    setProjectTitleDraft(currentProjectTitle);
    setIsEditingProjectTitle(true);
  };

  const cancelRenameProject = () => {
    setProjectTitleDraft('');
    setIsEditingProjectTitle(false);
  };

  const handleRenameProject = async () => {
    const nextTitle = projectTitleDraft.trim();
    if (!nextTitle) return;
    if (nextTitle === currentProjectTitle) {
      cancelRenameProject();
      return;
    }
    try {
      await updateProject({ projectId: projectId!, title: nextTitle }).unwrap();
      await refetchProject();
      cancelRenameProject();
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddTask = async () => {
    if (!newTaskSubject.trim()) return;
    try {
      await createTask({
        projectId: projectId!,
        subject: newTaskSubject.trim(),
        phase: newTaskPhase,
      }).unwrap();
      setNewTaskSubject('');
      setShowTaskForm(false);
      refetchTasks();
    } catch (e) {
      console.error(e);
    }
  };

  const handleTaskDone = async (taskId: string, done: boolean) => {
    try {
      await updateTask({ taskId, done }).unwrap();
      refetchTasks();
    } catch (e) {
      console.error(e);
    }
  };

  const handleArchiveTask = async (taskId: string) => {
    if (!window.confirm('Archive this task? You can permanently delete it from Archive.')) return;
    try {
      await updateTask({ taskId, archived: true }).unwrap();
      refetchTasks();
    } catch (e) {
      console.error(e);
    }
  };

  const formatDate = (d: string) => {
    if (!d) return '';
    try {
      return toLocaleDateStringInTimezone(
        d,
        'en-US',
        { month: 'long', day: 'numeric', year: 'numeric' },
        tenantTimezone
      );
    } catch {
      return d;
    }
  };

  if (!projectId || projectLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-3">
        <p className="text-muted">Project not found.</p>
        <Link to="/erm/projects">Back to Projects</Link>
      </div>
    );
  }

  return (
    <div className="erm-project-detail">
      <ERMSectionHeader title="Projects" backTo="/erm/projects" showHelp />
      <nav aria-label="breadcrumb" className="mb-2 mt-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item"><Link to="/erm/projects">Projects</Link></li>
          <li className="breadcrumb-item active">{project.title || 'Untitled'}</li>
        </ol>
      </nav>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {isEditingProjectTitle ? (
            <>
              <InputGroup size="sm" style={{ width: 'min(520px, 80vw)' }}>
                <Form.Control
                  value={projectTitleDraft}
                  onChange={(event) => setProjectTitleDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void handleRenameProject();
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      cancelRenameProject();
                    }
                  }}
                  placeholder="Project name"
                  autoFocus
                />
              </InputGroup>
              <Button
                variant="success"
                size="sm"
                className="d-inline-flex align-items-center justify-content-center"
                style={{ width: 30, height: 30, padding: 0 }}
                onClick={() => { void handleRenameProject(); }}
                disabled={isUpdatingProject || !projectTitleDraft.trim()}
                aria-label="Save project name"
              >
                <Check2 size={14} />
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="d-inline-flex align-items-center justify-content-center"
                style={{ width: 30, height: 30, padding: 0 }}
                onClick={cancelRenameProject}
                disabled={isUpdatingProject}
                aria-label="Cancel renaming project"
              >
                <X size={14} />
              </Button>
            </>
          ) : (
            <>
              <h4 className="mb-0">{currentProjectTitle}</h4>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none d-inline-flex align-items-center"
                onClick={startRenameProject}
                disabled={isUpdatingProject}
                aria-label="Rename project"
              >
                <PencilSquare size={14} />
              </Button>
            </>
          )}
          <span className="text-muted small fw-normal">Owner</span>
          <PersonCircle size={20} className="text-muted" />
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="success"
            size="sm"
            onClick={() => handleStatus('Completed')}
          >
            Completed
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleStatus('Canceled')}
          >
            Canceled
          </Button>
          <Dropdown align="end">
            <Dropdown.Toggle variant="outline-secondary" size="sm"><ThreeDotsVertical /></Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={startRenameProject} disabled={isUpdatingProject}>Rename project</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleArchiveProject} disabled={isUpdatingProject}>Archive project</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'plan')} className="mb-3">
        <Tab eventKey="plan" title="Plan" />
        <Tab eventKey="files" title="Files" />
        <Tab eventKey="notes" title="Notes" />
        <Tab eventKey="emails" title="Emails" />
        <Tab eventKey="documents" title="Documents" />
      </Tabs>

      {activeTab === 'plan' && (
        <div className="row g-3">
          <div className="col-md-4">
            <Card className="border">
              <Card.Header className="py-2 d-flex align-items-center justify-content-between">
                <span className="small fw-semibold">
                  <Button variant="link" size="sm" className="p-0 me-1" onClick={() => setSidebarOpen(!sidebarOpen)}>
                    {sidebarOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </Button>
                  Sidebar
                </span>
              </Card.Header>
              <Collapse in={sidebarOpen}>
                <Card.Body className="pt-0">
                  <div className="small">
                    <div className="mb-2">
                      <span className="text-muted">Delivery → </span>
                      {STAGES.map((s, i) => (
                        <span key={s}>
                          {i > 0 && ' → '}
                          <span className={stage === s ? 'fw-semibold' : ''}>{s}</span>
                        </span>
                      ))}
                    </div>
                    <div className="progress mb-2" style={{ height: 6 }}>
                      <div
                        className="progress-bar bg-success"
                        style={{ width: `${((stageIndex + 1) / STAGES.length) * 100}%` }}
                      />
                    </div>
                    <Badge bg="secondary" className="mb-2">{status}</Badge>
                    <div className="d-flex align-items-center gap-2 text-muted mb-2">
                      <Calendar3 size={14} />
                      {formatDate(project.startDate)} – {formatDate(project.endDate) || '—'}
                    </div>
                    <div className="fw-semibold mb-1">Linked to</div>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      <Button variant="outline-primary" size="sm"><Person size={12} /> Person <Plus size={10} /></Button>
                      <Button variant="outline-primary" size="sm"><Building size={12} /> Organization <Plus size={10} /></Button>
                      <Button variant="outline-primary" size="sm"><CurrencyDollar size={12} /> Deal <Plus size={10} /></Button>
                    </div>
                    <div>
                      <Button variant="link" size="sm" className="p-0 text-start" onClick={() => setDetailsOpen(!detailsOpen)}>
                        {detailsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Details
                      </Button>
                      <Collapse in={detailsOpen}>
                        <div className="small text-muted mt-1">
                          Description: {project.description || '—'}
                        </div>
                      </Collapse>
                    </div>
                  </div>
                </Card.Body>
              </Collapse>
            </Card>
          </div>
          <div className="col-md-8">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <div className="btn-group btn-group-sm">
                <Button variant={planView === 'list' ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setPlanView('list')}>
                  <ListUl size={14} /> List
                </Button>
                <Button variant={planView === 'gantt' ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setPlanView('gantt')}>
                  <BarChartSteps size={14} /> Gantt
                </Button>
              </div>
              <Button variant="success" size="sm" onClick={() => setShowTaskForm(true)}>
                <Plus size={14} /> Task
              </Button>
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">Phases</Dropdown.Toggle>
                <Dropdown.Menu>
                  {['Phase unassigned', ...STAGES].map((s) => (
                    <Dropdown.Item key={s} onClick={() => handleStage(s)}>{s}</Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <Button variant="link" size="sm"><Gear size={14} /></Button>
            </div>

            {showTaskForm && (
              <Card className="mb-3 border-primary">
                <Card.Body>
                  <Tabs defaultActiveKey="task">
                    <Tab eventKey="task" title="Task" />
                    <Tab eventKey="activity" title="Activity" />
                  </Tabs>
                  <Form.Group className="mt-2">
                    <Form.Label className="small">Task subject</Form.Label>
                    <Form.Control
                      placeholder="Task subject"
                      value={newTaskSubject}
                      onChange={(e) => setNewTaskSubject(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="mt-2">
                    <Form.Label className="small">Phase</Form.Label>
                    <Form.Select
                      value={newTaskPhase}
                      onChange={(e) => setNewTaskPhase(e.target.value)}
                    >
                      <option>Phase unassigned</option>
                      {STAGES.map((s) => <option key={s}>{s}</option>)}
                    </Form.Select>
                  </Form.Group>
                  <div className="d-flex justify-content-between mt-2">
                    <Button variant="link" size="sm">+ Subtask</Button>
                    <div>
                      <Button variant="secondary" size="sm" className="me-1" onClick={() => setShowTaskForm(false)}>Cancel</Button>
                      <Button variant="primary" size="sm" onClick={handleAddTask} disabled={!newTaskSubject.trim() || creatingTask}>
                        {creatingTask ? <Spinner animation="border" size="sm" /> : 'Save'}
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            {planView === 'list' && (
              <>
                {['Phase unassigned', ...STAGES].map((phase) => {
                  const phaseTasks: ErmTask[] = tasksByPhase[phase] || [];
                  const doneCount = phaseTasks.filter((t: ErmTask) => t.done).length;
                  return (
                    <Card key={phase} className="mb-3">
                      <Card.Header className="py-2 d-flex justify-content-between align-items-center">
                        <span className="fw-semibold small">
                          {phase} ({doneCount}/{phaseTasks.length})
                        </span>
                        <div className="d-flex gap-1">
                          <Button variant="link" size="sm" className="p-0" onClick={() => { setNewTaskPhase(phase); setShowTaskForm(true); }}>+ Task</Button>
                          <span className="text-muted">+ Activity</span>
                        </div>
                      </Card.Header>
                      <ListGroup variant="flush">
                        {tasksLoading ? (
                          <ListGroup.Item><Spinner animation="border" size="sm" /></ListGroup.Item>
                        ) : (
                          phaseTasks.map((task: ErmTask) => (
                            <ListGroup.Item key={task.id} className="d-flex align-items-center gap-2">
                              <Form.Check
                                type="checkbox"
                                checked={!!task.done}
                                onChange={(e) => handleTaskDone(task.id, e.target.checked)}
                              />
                              <span className={task.done ? 'text-decoration-line-through text-muted' : ''}>{task.subject}</span>
                              <span className="ms-auto small text-muted">{task.assigneeId ? 'Assignee' : ''}</span>
                              <span className="small text-muted">{task.dueDate ? formatDate(task.dueDate) : ''}</span>
                              <Dropdown align="end" onClick={(e) => e.preventDefault()}>
                                <Dropdown.Toggle variant="link" size="sm" className="text-dark p-0"><ThreeDotsVertical size={14} /></Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => handleArchiveTask(task.id)}>Archive</Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </ListGroup.Item>
                          ))
                        )}
                      </ListGroup>
                    </Card>
                  );
                })}
              </>
            )}

            {planView === 'gantt' && (
              <Card>
                <Card.Body className="text-muted text-center py-5">Gantt view coming soon.</Card.Body>
              </Card>
            )}
          </div>
        </div>
      )}

      {activeTab !== 'plan' && (
        <Card>
          <Card.Body className="text-muted text-center py-5">
            {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} tab coming soon.
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
